import {
  areaOfCircle,
  MAX_RADIUS_FACTOR,
  NamedNumericRange,
  NumericRange,
  radiusOfCircle,
  radiusScaler,
  radiusUnscaler,
  rect,
  xScaler,
  xUnscaler,
  yScaler,
  yUnscaler,
} from '@ardoq/graph';
import {
  BUBBLE_SLIDER_OPTIONS,
  BUBBLE_SLIDER_RANGE,
  DISPLAYED_LABELS_LIMIT,
} from './consts';
import {
  BubbleChartBubbleModel,
  BubbleChartLabelData,
  BubbleChartRenderingData,
  BubbleChartRenderingParameters,
  BubbleChartViewSettings,
  ChartChrome,
  ChartScaleResult,
} from './types';
import { getBubbleChartAxisProps } from './chartUtils';
import { getQuadrantOverlayProps } from './calculations';
import {
  applyInteractiveStatesToData,
  getDecollidedDataPointLabels,
  positionAndCullDataPoints,
  sizeDataPoints,
} from './chartAreaUtils';
import { getZoomControlsProps } from './zoomControls/utils';
import { getBubbleChartViewLegendContents } from './legend/getBubbleChartViewLegendContents';

type ScaleFactory = (params: {
  domain: NamedNumericRange; // input
  range: NamedNumericRange; // output
}) => (x: number) => number;
export const bubbleAreaScaleFactory: ScaleFactory = ({ domain, range }) => {
  const norm = (({ min, max }: NamedNumericRange) => {
    const extent = max - min;
    return (x: number) => (x - min) / extent;
  })(domain);
  const base = (({ min, max }: NamedNumericRange) => {
    const invertMin = 1 / min;
    return (x: number) => invertMin - (invertMin - max) * norm(x);
  })(range);
  const exponent = (({ min, max }: NamedNumericRange) => {
    const extent = max - min;
    const middle = min + extent / 2;
    return (x: number) => (x - middle) / extent;
  })(domain);
  return x => base(x) ** exponent(x);
};

const bubbleAreaScale = bubbleAreaScaleFactory({
  domain: BUBBLE_SLIDER_OPTIONS,
  range: BUBBLE_SLIDER_RANGE,
});

const FULLY_OPAQUE_LABELS = 20;
const MIN_FONT_SIZE = 10;
const MAX_FONT_SIZE = 24;
const MIN_OPACITY = 0.05;

// regarding transition-duration: the labels fade in slower than the bubbles. otherwise, labels will suddenly appear under the mouse and steal the user's attention by triggering the hover/focus effect.

export const resolveShowBubblesWithZeroValue = ({
  showBubblesWithZeroValue,
}: BubbleChartViewSettings): boolean =>
  // treat undefined as true: an unset value from an older user profile or presentation slide. the default value of the setting is true.
  showBubblesWithZeroValue === undefined || showBubblesWithZeroValue;

export const buildScalers = (
  renderingData: BubbleChartRenderingData
): ChartScaleResult => {
  const { dimensions, domain, windowCenter, windowScale, viewSettings } =
    renderingData;
  const data = renderingData.selectedQuadrantData ?? renderingData.data;

  const { left, right, top, bottom } = rect(windowCenter, windowScale);

  const { bubbleAreaScaleFactor } = viewSettings;

  const minDimension = Math.min(
    dimensions.chart.height,
    dimensions.chart.width
  );
  const zeroRadiusDomain = domain.radius[0] === domain.radius[1];
  const maxRadius = zeroRadiusDomain
    ? minDimension * 0.01 // bubbles should be small if there's no Z field.
    : minDimension * MAX_RADIUS_FACTOR;
  const maxArea = areaOfCircle(maxRadius);
  const minArea = zeroRadiusDomain
    ? maxArea
    : maxArea * Math.max(0, domain.radius[0] / domain.radius[1]); // bubbles can't be < 0px
  const minRadius = radiusOfCircle(minArea);

  const axisMargin = Math.ceil(maxRadius);
  const xAxisPixelRange: NumericRange = [
    axisMargin,
    dimensions.chart.width - axisMargin,
  ];
  const yAxisPixelRange: NumericRange = [
    axisMargin,
    dimensions.chart.height - axisMargin,
  ];

  const scaleX = xScaler(xAxisPixelRange, domain.x, [left, right]);
  const scaleY = yScaler(yAxisPixelRange, domain.y, [top, bottom]);
  const unscaleX = xUnscaler(xAxisPixelRange, domain.x, [left, right]);
  const unscaleY = yUnscaler(yAxisPixelRange, domain.y, [top, bottom]);

  const maxRadiusScale = bubbleAreaScale(bubbleAreaScaleFactor);
  const radiusScale = windowScale / maxRadiusScale;
  const scaleRadius = radiusScaler(
    [minRadius, maxRadius],
    domain.radius,
    radiusScale
  );
  const unscaleRadius = radiusUnscaler(
    [minRadius, maxRadius],
    domain.radius,
    radiusScale
  );

  const scaleFontSize = radiusScaler(
    [MIN_FONT_SIZE, MAX_FONT_SIZE],
    domain.labeledBubblesRadius
  );
  const scaleFontOpacity = radiusScaler(
    [MIN_OPACITY, 1],
    data.length <= FULLY_OPAQUE_LABELS
      ? [-1, 0]
      : [0, Math.min(DISPLAYED_LABELS_LIMIT, data.length) - FULLY_OPAQUE_LABELS]
  );

  return {
    scalers: {
      fontSize: scaleFontSize,
      fontOpacity: scaleFontOpacity,
      radius: value => (value ? scaleRadius(value) : 0),
      unscaleRadius,
      y: scaleY,
      x: scaleX,
    },
    visibleRangeX: [unscaleX(xAxisPixelRange[0]), unscaleX(xAxisPixelRange[1])],
    visibleRangeY: [unscaleY(yAxisPixelRange[1]), unscaleY(yAxisPixelRange[0])],
  };
};

const buildChartChrome = (
  renderingData: BubbleChartRenderingData,
  { scalers, visibleRangeX, visibleRangeY }: ChartScaleResult,
  chartAreaClipPathUrl: string
): ChartChrome => {
  const {
    dimensions,
    domain,
    margin,
    viewSettings,
    xAxisTitle,
    yAxisTitle,
    radiusFieldData,
    bubbleChartCommands,
    viewInstanceId,
    quadrantSettings,
    isScopeDiffMode,
    activeDiffMode,
    quadrantZoomState,
    isPresentationMode,
    viewpointModeAttributes,
    locale,
    i18nLocale,
    users,
    selectedQuadrantDropdownLabels,
    timeLayoutLabels,
    windowCenter,
    windowScale,
  } = renderingData;
  const { background } = viewSettings;

  const { yAxisProps, xAxisProps } = getBubbleChartAxisProps({
    domain,
    dimensions,
    margin,
    scaleX: scalers.x,
    scaleY: scalers.y,
    background,
    xAxisTitle,
    yAxisTitle,
    visibleRangeX,
    visibleRangeY,
    locale,
  });

  const quadrantOverlayProps = quadrantSettings
    ? getQuadrantOverlayProps({
        chartWidth: dimensions.chart.width,
        chartHeight: dimensions.chart.height,
        scaleX: scalers.x,
        scaleY: scalers.y,
        viewInstanceId,
        xRange: domain.x,
        yRange: domain.y,
        chartAreaClipPathUrl,
        margin,
        quadrantSettings,
        selectedQuadrant: quadrantZoomState.selectedQuadrant,
      })
    : null;

  // Show zoom controls in presentation mode or workspace mode.
  const zoomControlsProps =
    isPresentationMode || !viewpointModeAttributes
      ? getZoomControlsProps({
          viewSettings,
          quadrantZoomState,
          viewInstanceId,
          windowState: { windowCenter, windowScale },
          bubbleChartCommands,
          hideQuadrantControls: !isPresentationMode,
          selectedQuadrantDropdownLabels,
          timeLayoutLabels,
        })
      : null;

  const viewLegendContent = getBubbleChartViewLegendContents({
    data: renderingData.selectedQuadrantData ?? renderingData.data,
    radiusFieldData,
    domain,
    scalers,
    activeDiffMode,
    isScopeDiffMode,
    locale,
    i18nLocale,
    showBubblesWithZeroValue: resolveShowBubblesWithZeroValue(viewSettings),
    isViewpointMode: viewpointModeAttributes !== undefined,
    users,
  });

  return {
    xAxisProps,
    yAxisProps,
    quadrantOverlayProps,
    zoomControlsProps,
    viewLegendContent,
  };
};

export const assembleRenderingParameters = (
  renderingData: BubbleChartRenderingData,
  scaleContext: ChartScaleResult,
  sizedDataPoints: BubbleChartBubbleModel[],
  positionedDataPoints: BubbleChartBubbleModel[],
  decollidedLabels: BubbleChartLabelData[]
): BubbleChartRenderingParameters => {
  const { dimensions, margin, hoverState, focusState, viewInstanceId } =
    renderingData;
  const chartAreaClipPathUrl = `chartAreaClipPath-${viewInstanceId}`;

  const { renderedDataPoints, renderedLabels } = applyInteractiveStatesToData({
    dataPoints: positionedDataPoints,
    labels: decollidedLabels,
    hoverState,
    focusState,
    dimensions,
  });

  return {
    ...buildChartChrome(renderingData, scaleContext, chartAreaClipPathUrl),
    scalers: scaleContext.scalers,
    visibleRangeX: scaleContext.visibleRangeX,
    visibleRangeY: scaleContext.visibleRangeY,
    chartAreaClipPathUrl,
    sizedDataPoints,
    positionedDataPoints,
    decollidedLabels,
    chartAreaProps: {
      clipPathUrl: chartAreaClipPathUrl,
      margin,
      renderedDataPoints,
      renderedLabels,
    },
  };
};

export const createRenderingParameters = (
  renderingData: BubbleChartRenderingData
): BubbleChartRenderingParameters => {
  const scaleContext = buildScalers(renderingData);
  const sizedDataPoints = sizeDataPoints({
    data: renderingData.selectedQuadrantData ?? renderingData.data,
    scalers: scaleContext.scalers,
    viewSettings: renderingData.viewSettings,
    isViewpointMode: !!renderingData.viewpointModeAttributes,
  });
  const positionedDataPoints = positionAndCullDataPoints({
    sizedDataPoints,
    scalers: scaleContext.scalers,
    dimensions: renderingData.dimensions,
    showBubblesWithZeroValue: resolveShowBubblesWithZeroValue(
      renderingData.viewSettings
    ),
  });
  const decollidedLabels = getDecollidedDataPointLabels({
    positionedDataPoints,
    dimensions: renderingData.dimensions,
  });
  return assembleRenderingParameters(
    renderingData,
    scaleContext,
    sizedDataPoints,
    positionedDataPoints,
    decollidedLabels
  );
};
